import React from "react";
import { Suggestion } from "../hooks/useVoiceWidget";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";

interface SuggestionCardProps {
  suggestion: Suggestion;
  theme?: any;
  uiVariant?: "legacy" | "split-switch";
  /** When false, non-booking link cards render without a clickable button. */
  linkButtonsEnabled?: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  theme,
  uiVariant = "legacy",
  linkButtonsEnabled = true,
}) => {
  const useNeutralTextWindow = uiVariant === "split-switch";
  // Booking buttons always show; other (agent-suggested) link buttons can be
  // hidden per deployment when their URLs aren't reliable — the card then stays
  // as a static info block.
  const showAction =
    !!suggestion.action_url && (linkButtonsEnabled || suggestion.type === "booking");

  const handleClick = () => {
    if (showAction) {
      window.open(suggestion.action_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleClick();
  };

  const getIcon = () => {
    switch (suggestion.type) {
      case "location":
        return "📍";
      case "event":
        return "🎉";
      case "product":
        return "🛍️";
      case "link":
        return "🔗";
      case "booking":
        return "🔗";
      case "info":
        return "ℹ️";
      default:
        return "💡";
    }
  };

  const getTypeLabel = () => {
    switch (suggestion.type) {
      case "location":
        return "Location";
      case "event":
        return "Event";
      case "product":
        return "Product";
      case "link":
        return "Link";
      case "booking":
        return "Booking";
      case "info":
        return "Info";
      default:
        return "Suggestion";
    }
  };

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${
      useNeutralTextWindow ? NEUTRAL_PALETTE.mist : theme?.chatBorderColor || "#e2e8f0"
    }`,
    borderRadius: useNeutralTextWindow
      ? "10px"
      : theme?.borderRadius || "12px",
    padding: "12px",
    backgroundColor: useNeutralTextWindow
      ? "#ffffff"
      : theme?.chatBackground || "#ffffff",
    cursor: showAction ? "pointer" : "default",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    boxShadow: useNeutralTextWindow
      ? "0 1px 2px rgba(94, 93, 89, 0.06)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const iconStyle: React.CSSProperties = {
    fontSize: "18px", // Slightly larger icon
  };

  const titleStyle: React.CSSProperties = {
    fontWeight: "600",
    color: useNeutralTextWindow ? NEUTRAL_PALETTE.anthracite : theme?.primaryColor || "#6366f1",
    fontSize: "16px", // Increased from 14px
    margin: 0,
  };

  const typeStyle: React.CSSProperties = {
    fontSize: "12px", // Increased from 11px
    color: useNeutralTextWindow ? NEUTRAL_PALETTE.slate : theme?.agentMessageColor || "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontWeight: "500",
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: "14px", // Increased from 13px
    color: useNeutralTextWindow ? NEUTRAL_PALETTE.anthracite : theme?.agentMessageColor || "#1e293b",
    lineHeight: "1.4",
    margin: 0,
  };

  const actionStyle: React.CSSProperties = {
    marginTop: "10px",
    fontSize: "14px", // Increased from 12px
    color: "#ffffff",
    backgroundColor: useNeutralTextWindow
      ? NEUTRAL_PALETTE.anthracite
      : theme?.primaryColor || "#6366f1",
    fontWeight: "500",
    border: "0",
    borderRadius: useNeutralTextWindow ? "6px" : theme?.borderRadius || "8px",
    padding: "9px 12px",
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
  };

  const actionLabel =
    suggestion.action_label || (suggestion.type === "booking" ? "Book now" : "Open");

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      onMouseEnter={(e) => {
        if (showAction) {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = useNeutralTextWindow
            ? "0 4px 10px rgba(94, 93, 89, 0.10)"
            : "0 4px 12px rgba(0, 0, 0, 0.15)";
          if (useNeutralTextWindow) {
            e.currentTarget.style.borderColor = NEUTRAL_PALETTE.stone;
          }
        }
      }}
      onMouseLeave={(e) => {
        if (showAction) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = useNeutralTextWindow
            ? "0 1px 2px rgba(94, 93, 89, 0.06)"
            : "0 1px 3px rgba(0, 0, 0, 0.1)";
          if (useNeutralTextWindow) {
            e.currentTarget.style.borderColor = NEUTRAL_PALETTE.mist;
          }
        }
      }}
    >
      <div style={headerStyle}>
        <span style={iconStyle}>{getIcon()}</span>
        <div style={{ flex: 1 }}>
          <h4 style={titleStyle}>{suggestion.title}</h4>
          <div style={typeStyle}>{getTypeLabel()}</div>
        </div>
      </div>

      <p style={descriptionStyle}>{suggestion.description}</p>

      {showAction && (
        <button type="button" style={actionStyle} onClick={handleActionClick}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default SuggestionCard;
